import React from 'react';
import { styled } from '@mui/material/styles';
import { Typography } from '@mui/material';
import { NODE_UI, stopPropagation } from './nodeUiStyles';

/**
 * NodeContainer — outer shell for every inline node config.
 * Provides layout, scroll container, section spacing, and event isolation.
 *
 * Usage:
 *   <NodeContainer label="Send Email" icon="/svgs/send_email.svg">
 *     <NodeSelect ... />
 *     <NodePlaceholderTextField ... />
 *   </NodeContainer>
 */

const Root = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: NODE_UI.spacing.md,
  width: '100%',
  padding: `${NODE_UI.spacing.md} 0`,
}));

const HeaderRow = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  padding: `0 ${NODE_UI.spacing.xs}`,
});

const IconImg = styled('img')({
  width: 18,
  height: 18,
  flexShrink: 0,
});

const SectionLabel = styled(Typography)(({ theme }) => ({
  fontSize: NODE_UI.fontSize.label,
  fontWeight: NODE_UI.fontWeight.label,
  color: theme.palette.text.secondary,
  lineHeight: 1.4,
  userSelect: 'none',
}));

const NodeContainer = React.memo(function NodeContainer({
  label,
  icon,
  showHeader = false,
  children,
  sx,
  /** On React Flow canvas nodes: nodrag/nopan/nowheel so inputs open instead of panning/dragging. */
  canvas = false,
}) {
  return (
    <Root
      className={canvas ? "nodrag nopan nowheel" : undefined}
      onMouseDown={stopPropagation}
      onClick={stopPropagation}
      onPointerDown={stopPropagation}
      onWheel={canvas ? stopPropagation : undefined}
      sx={sx}
    >
      {showHeader && (label || icon) && (
        <HeaderRow>
          {icon && <IconImg src={icon} alt="" />}
          {label && <SectionLabel>{label}</SectionLabel>}
        </HeaderRow>
      )}
      {children}
    </Root>
  );
});

export default NodeContainer;
